import { useEffect, useCallback } from 'react';

import { useAppDispatch } from 'store/hooks';
import { setOnline, setOffline, setNetworkStatus } from 'store/system';

/**
 * Hook khởi tạo lắng nghe sự kiện mạng và đồng bộ trạng thái vào Redux store.
 *
 * - Dispatch `setNetworkStatus` ngay khi mount để đồng bộ `navigator.onLine`.
 * - Lắng nghe sự kiện `online` / `offline` của browser rồi dispatch action tương ứng.
 * - Không trả về giá trị — component đọc state qua `useAppSelector`.
 *
 * @example
 * // Gắn một lần duy nhất ở component gốc (e.g. App.tsx)
 * useNetworkStatus();
 */
const useNetworkStatus = (): void => {
  const dispatch = useAppDispatch();

  const handleOnline = useCallback((): void => {
    dispatch(setOnline());
  }, [dispatch]);

  const handleOffline = useCallback((): void => {
    dispatch(setOffline());
  }, [dispatch]);

  useEffect(() => {
    // Sync trạng thái ban đầu khi component mount
    dispatch(setNetworkStatus(navigator.onLine));

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return (): void => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [dispatch, handleOnline, handleOffline]);
};

export default useNetworkStatus;
